import React from 'react';
import { Box, Card, CardContent, Typography, Chip, Stack, Button, Select, MenuItem, FormControl, FormControlLabel, InputLabel, Switch } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import { User } from '../users/redux/usersSlice';
import { TOPICS } from '../vectorSpace/topics';


interface Vector3DPoint {
  id: string;
  x: number;
  y: number;
  z: number;
  topic: string;
  document: string;
}

interface VectorSpaceControlsProps {
  visibleTopics: Set<string>;
  onToggleTopic: (topic: string) => void;
  onRefresh: () => void;
  onResetCamera: () => void;
  onCenterView?: () => void;
  vectorCount: number;
  visibleCount: number;
  points?: Vector3DPoint[];
  users?: User[];
  selectedUserId?: string | null;
  onUserChange?: (userId: string | null) => void;
  showLinks?: boolean;
  onToggleLinks?: (show: boolean) => void;
}

export const VectorSpaceControls: React.FC<VectorSpaceControlsProps> = ({
  visibleTopics,
  onToggleTopic,
  onRefresh,
  onResetCamera,
  onCenterView = () => {},
  vectorCount,
  visibleCount,
  points = [],
  users = [],
  selectedUserId = null,
  onUserChange = () => {},
  showLinks = false,
  onToggleLinks = () => {},
}) => {
  const getTopicCount = (topic: string): number => {
    return points.filter(p => p.topic === topic).length;
  };
  const allVisible = visibleTopics.size === TOPICS.length;

  const handleToggleAll = () => {
    if (allVisible) {
      // Deselect all
      TOPICS.forEach(t => {
        if (visibleTopics.has(t.name)) onToggleTopic(t.name);
      });
    } else {
      // Select all
      TOPICS.forEach(t => {
        if (!visibleTopics.has(t.name)) onToggleTopic(t.name);
      });
    }
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
      <CardContent sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem', fontWeight: 600 }}>
          Vector Space Manager
        </Typography>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel size="small">Filter by User</InputLabel>
          <Select
            value={selectedUserId || ''}
            label="Filter by User"
            onChange={(e) => onUserChange(e.target.value || null)}
            size="small"
          >
            <MenuItem value="">All Users</MenuItem>
            {users.map(user => (
              <MenuItem key={user.id} value={user.id}>
                {user.display_name || user.email}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ mb: 2 }}>
          <Stack direction="row" gap={1} sx={{ mb: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={onRefresh}
              fullWidth
            >
              Refresh
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<CenterFocusStrongIcon />}
              onClick={onCenterView}
              fullWidth
            >
              Center
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<CenterFocusStrongIcon />}
              onClick={onResetCamera}
              fullWidth
            >
              Reset
            </Button>
          </Stack>
        </Box>

        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={<Switch size="small" checked={showLinks} onChange={(e) => onToggleLinks(e.target.checked)} />}
            label={<Typography variant="body2">Nearest-neighbour links</Typography>}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Joins each point to its 2 closest points. Shows clusters; the lines carry no order or time.
          </Typography>
        </Box>

        <Box sx={{ mb: 3, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.85rem' }}>
            Visible Vectors
          </Typography>
          <Typography variant="h6" sx={{ fontSize: '1.5rem', fontWeight: 700 }}>
            {visibleCount} / {vectorCount}
          </Typography>
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, fontSize: '0.875rem' }}>
          Topics
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Button
            size="small"
            variant={allVisible ? 'contained' : 'outlined'}
            onClick={handleToggleAll}
            fullWidth
            sx={{ mb: 1, fontSize: '0.75rem' }}
          >
            {allVisible ? 'Hide All' : 'Show All'}
          </Button>
        </Box>

        <Stack spacing={1}>
          {TOPICS.map(topic => (
            <Box
              key={topic.name}
              onClick={() => onToggleTopic(topic.name)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1,
                borderRadius: 1,
                cursor: 'pointer',
                bgcolor: visibleTopics.has(topic.name) ? 'action.selected' : 'action.hover',
                border: '1px solid',
                borderColor: visibleTopics.has(topic.name) ? 'primary.main' : 'divider',
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: 'action.selected',
                  borderColor: 'primary.main',
                },
              }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: topic.color,
                  opacity: visibleTopics.has(topic.name) ? 1 : 0.4,
                  transition: 'opacity 0.2s',
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.875rem',
                  flex: 1,
                  textTransform: 'capitalize',
                  opacity: visibleTopics.has(topic.name) ? 1 : 0.6,
                  transition: 'opacity 0.2s',
                }}
              >
                {topic.name}
              </Typography>
              <Chip
                label={getTopicCount(topic.name)}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: '0.7rem',
                  height: 20,
                  opacity: visibleTopics.has(topic.name) ? 1 : 0.5,
                }}
              />
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};
